import axios from 'axios';

// Usar proxy relativo via Apache quando em produção
const isProduction = window.location.hostname !== 'localhost';
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (isProduction ? '/api' : 'http://localhost:3001/api');

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token nas requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Serviços de autenticação
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/usuarios/login', credentials);
    return response.data;
  },
  
  verifyToken: async () => {
    const response = await api.get('/usuarios/verify');
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

// Serviços de usuários
export const usuarioService = {
  getAll: async () => {
    const response = await api.get('/usuarios');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/usuarios/${id}`);
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/usuarios', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await api.put(`/usuarios/${id}`, userData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/usuarios/${id}`);
    return response.data;
  }
};

// Serviços de franquias
export const franquiaService = {
  getAll: async (includeCount = false) => {
    const response = await api.get(`/franquias?includeCount=${includeCount}`);
    return response.data;
  },
  
  getActive: async () => {
    const response = await api.get('/franquias/ativas');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/franquias/${id}`);
    return response.data;
  },
  
  getByCnpj: async (cnpj) => {
    const response = await api.get(`/franquias/cnpj/${cnpj}`);
    return response.data;
  },
  
  create: async (franquiaData) => {
    const response = await api.post('/franquias', franquiaData);
    return response.data;
  },
  
  update: async (id, franquiaData) => {
    const response = await api.put(`/franquias/${id}`, franquiaData);
    return response.data;
  },
  
  toggleStatus: async (id, novoStatus) => {
    const response = await api.patch(`/franquias/${id}/toggle-status`, { novoStatus });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/franquias/${id}`);
    return response.data;
  }
};

// Serviços de históricos
export const historicoService = {
  getAll: async () => {
    const response = await api.get('/historicos');
    return response.data;
  },
  
  getByPeriodo: async (periodo) => {
    const response = await api.get(`/historicos/periodo/${periodo}`);
    return response.data;
  },
  
  getPeriodosDisponiveis: async () => {
    const response = await api.get('/historicos/periodos');
    return response.data;
  },
  
  getResumoByPeriodo: async (periodo) => {
    const response = await api.get(`/historicos/resumo/${periodo}`);
    return response.data;
  },

  getComparativo: async (periodoA, periodoB) => {
    const response = await api.get(`/historicos/comparativo/${periodoA}/${periodoB}`);
    return response.data;
  },

  getByFranquiaAndPeriodo: async (franquiaId, periodo) => {
    const response = await api.get(`/historicos/franquia/${franquiaId}/periodo/${periodo}`);
    return response.data;
  },
  
  // Função para popular histórico com progresso em tempo real
  popularHistorico: (onProgress) => {
    return new Promise((resolve, reject) => {
      // Iniciar o processo de população
      api.post('/historicos/popular-start')
        .then(() => {
          // Criar EventSource para receber atualizações de progresso
          const eventSource = new EventSource(`${API_BASE_URL}/sse/historicos/popular-progress`);

          eventSource.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              
              if (data.type === 'progress') {
                onProgress?.(data);
              } else if (data.type === 'complete') {
                eventSource.close();
                resolve(data);
              } else if (data.type === 'error') {
                eventSource.close();
                reject(new Error(data.message));
              }
            } catch (error) {
              eventSource.close();
              reject(new Error('Erro ao processar dados do servidor'));
            }
          };

          eventSource.onerror = () => {
            eventSource.close();
            reject(new Error('Erro na conexão com o servidor'));
          };
        })
        .catch(reject);
    });
  },

  // Buscar dados completos do dashboard com totais por empresa
  getDashboardCompleto: async () => {
    const response = await api.get('/historicos/dashboard-completo');
    return response.data;
  },

  // Buscar empresas por franquia e período
  getEmpresasByFranquiaAndPeriodo: async (id_franquia, mm_yyyy) => {
    const response = await api.get(`/historicos/empresas/${id_franquia}/${mm_yyyy}`);
    return response.data;
  },

  // Buscar histórico de uma empresa
  getHistoricoEmpresa: async (ex_empresa, id_franquia) => {
    const response = await api.get(`/empresas/historico/${ex_empresa}/${id_franquia}`);
    return response.data;
  },

  // Buscar histórico de uma franquia com dados por empresa
  getHistoricoFranquiaComEmpresas: async (id_franquia) => {
    const response = await api.get(`/empresas/historico-franquia/${id_franquia}`);
    return response.data;
  }
};

export default api;
